import {
	Input,
	Select,
	Button,
	InputNumber,
	Slider,
	Typography,
	Checkbox,
	Radio,
} from 'antd';
import { FilterFilled } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { type LocationLabelFormatter, locationSearchText } from '../../utils/locationLabel';

interface ColumnFilterProps {
	queryParams: URLSearchParams;
	navigate: any;
	paramKey: string;
	currentValue?: string;
}

export const getColumnSearchFilter = (props: ColumnFilterProps) => {
	return {
		filterDropdown: ({ close }: any) => {
			const [value, setValue] = useState(props.currentValue || '');
			const isFilterActive = !!props.currentValue;

			useEffect(() => {
				setValue(props.currentValue || '');
			}, [props.currentValue]);

			const handleOk = () => {
				const trimmedValue = value.trim();
				if (trimmedValue) {
					props.queryParams.set(props.paramKey, trimmedValue);
				} else {
					props.queryParams.delete(props.paramKey);
				}
				props.queryParams.set('page', '1');
				props.navigate({ search: props.queryParams.toString() });
				close();
			};

			const handleReset = () => {
				setValue('');
				props.queryParams.delete(props.paramKey);
				props.queryParams.set('page', '1');
				props.navigate({ search: props.queryParams.toString() });
				close();
			};

			return (
				<div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
					<Input
						placeholder="Search"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						onPressEnter={handleOk}
						style={{ marginBottom: 8, display: 'block', width: 200 }}
					/>
					<div
						style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
					>
						<Button
							onClick={handleReset}
							size="small"
							type="text"
							disabled={!isFilterActive}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
								padding: 0,
							}}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleOk}
							size="small"
							style={{ width: 70 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: () => (
			<FilterFilled
				style={{ color: props.currentValue ? '#1890ff' : undefined }}
			/>
		),
	};
};

export const getColumnMultiSelectFilter = (
	props: ColumnFilterProps & {
		options: { text: string; value: string }[];
		selectAllOnReset?: boolean;
	}
) => {
	return {
		filterDropdown: ({ close }: any) => {
			const [selectedValues, setSelectedValues] = useState<string[]>(
				props.currentValue ? props.currentValue.split(',') : []
			);
			const isFilterActive = !!props.currentValue;

			useEffect(() => {
				const valuesFromUrl = props.currentValue
					? props.currentValue.split(',').filter(Boolean)
					: [];
				setSelectedValues(valuesFromUrl);
			}, [props.currentValue]);

			const handleOk = () => {
				if (selectedValues.length > 0) {
					props.queryParams.set(props.paramKey, selectedValues.join(','));
				} else {
					props.queryParams.delete(props.paramKey);
				}
				props.queryParams.set('page', '1');
				props.navigate({ search: props.queryParams.toString() });
				close();
			};

			const handleReset = () => {
				if (props.selectAllOnReset) {
					const allVals = props.options.map((o) => o.value);
					setSelectedValues(allVals);
					props.queryParams.set(props.paramKey, allVals.join(','));
				} else {
					setSelectedValues([]);
					props.queryParams.delete(props.paramKey);
				}
				props.queryParams.set('page', '1');
				props.navigate({ search: props.queryParams.toString() });
				close();
			};

			return (
				<div
					style={{ padding: 8, minWidth: 200 }}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<div
						style={{
							border: '1px solid #d9d9d9',
							borderRadius: 4,
							maxHeight: 200,
							overflowY: 'auto',
							padding: '4px 8px',
							marginBottom: 8,
						}}
					>
						<Checkbox.Group
							value={selectedValues}
							onChange={(values) => setSelectedValues(values as string[])}
							style={{
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{props.options.map((option) => (
								<Checkbox
									key={option.value}
									value={option.value}
									style={{ margin: '4px 0', marginLeft: 0 }}
								>
									{option.text}
								</Checkbox>
							))}
						</Checkbox.Group>
					</div>
					<div
						style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
					>
						<Button
							onClick={handleReset}
							size="small"
							type="text"
							disabled={!isFilterActive}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
								padding: 0,
							}}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleOk}
							size="small"
							style={{ width: 70 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: () => (
			<FilterFilled
				style={{ color: props.currentValue ? '#1890ff' : undefined }}
			/>
		),
	};
};

export const getColumnGroupedSelectFilter = (
	props: ColumnFilterProps & { locations: any[]; formatLocation?: LocationLabelFormatter }
) => {
	const formatLocation: LocationLabelFormatter = props.formatLocation ?? ((text) => text);
	return {
		filterDropdown: ({ close }: any) => {
			const [tempValue, setTempValue] = useState<string[]>(
				props.currentValue ? props.currentValue.split(',').filter(Boolean) : []
			);
			const [searchValue, setSearchValue] = useState('');
			const isFilterActive = !!props.currentValue;

			// Group locations by rack name INSIDE the component
			type LocationsGroupedByRack = Record<string, any[]>;
			const locationsGroupedByRack: LocationsGroupedByRack = useMemo(
				() =>
					(props.locations || []).reduce(
						(acc: LocationsGroupedByRack, location: any) => {
							const rackName =
								location.shelf?.rack?.rack_name || 'Unknown Rack';
							if (!acc[rackName]) acc[rackName] = [];
							acc[rackName].push(location);
							return acc;
						},
						{}
					),
				[props.locations]
			);

			useEffect(() => {
				const valuesFromUrl = props.currentValue
					? props.currentValue.split(',').filter(Boolean)
					: [];
				setTempValue(valuesFromUrl);
			}, [props.currentValue]);

			// Filter grouped locations by search term
			const filteredGroupedLocations = useMemo(() => {
				if (!searchValue.trim()) return locationsGroupedByRack;

				const search = searchValue.toLowerCase();
				const filtered: LocationsGroupedByRack = {};

				for (const [rackName, locations] of Object.entries(
					locationsGroupedByRack
				)) {
					const matchingLocations = locations.filter(
						(loc: any) =>
							locationSearchText(loc.display_name, formatLocation).toLowerCase().includes(search) ||
							rackName.toLowerCase().includes(search)
					);
					if (matchingLocations.length > 0) {
						filtered[rackName] = matchingLocations;
					}
				}
				return filtered;
			}, [searchValue, locationsGroupedByRack, formatLocation]);

			const handleApply = () => {
				if (tempValue.length > 0) {
					props.queryParams.set(props.paramKey, tempValue.join(','));
				} else {
					props.queryParams.delete(props.paramKey);
				}
				props.queryParams.set('page', '1');
				props.navigate({ search: props.queryParams.toString() });
				close();
			};

			const handleReset = () => {
				setTempValue([]);
				setSearchValue('');
				props.queryParams.delete(props.paramKey);
				props.queryParams.set('page', '1');
				props.navigate({ search: props.queryParams.toString() });
				close();
			};

			return (
				<div
					style={{ minWidth: 250, padding: 8 }}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<Input
						placeholder="Search location..."
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						style={{ marginBottom: 8 }}
						allowClear
					/>

					<div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 8 }}>
						{Object.keys(filteredGroupedLocations).length > 0 ? (
							Object.entries(filteredGroupedLocations).map(
								([rackName, rackLocations]) => (
									<div key={rackName} style={{ marginBottom: 12 }}>
										<div style={{ fontWeight: 'bold', marginBottom: 4 }}>
											{rackName}
										</div>
										{rackLocations.map((location: any) => (
											<div
												key={location.id}
												style={{ marginBottom: 4, marginLeft: 8 }}
											>
												<Checkbox
													checked={tempValue.includes(location.id)}
													onChange={(e) => {
														if (e.target.checked) {
															setTempValue([...tempValue, location.id]);
														} else {
															setTempValue(
																tempValue.filter((id) => id !== location.id)
															);
														}
													}}
												>
													{formatLocation(location.display_name)}
												</Checkbox>
											</div>
										))}
									</div>
								)
							)
						) : (
							<div
								style={{
									textAlign: 'center',
									color: '#999',
									padding: '20px 0',
								}}
							>
								No locations found
							</div>
						)}
					</div>

					<div
						style={{
							borderTop: '1px solid #f0f0f0',
							paddingTop: 8,
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
						}}
					>
						<Button
							onClick={handleReset}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
							size="small"
							type="text"
							disabled={!isFilterActive}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleApply}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: () => (
			<FilterFilled
				style={{ color: props.currentValue ? '#1890ff' : undefined }}
			/>
		),
	};
};

export const getColumnRangeFilter = ({
	queryParams,
	navigate,
	minParamKey,
	maxParamKey,
	currentMinValue,
	currentMaxValue,
	label = '',
	unit = 'in',
	min = 0,
	max = 1000,
}: any) => {
	const hasValidRange = min !== 0 || max !== 999;

	return {
		filterDropdown: ({ confirm, clearFilters }: any) => {
			const [minVal, setMinVal] = useState<number | null>(
				currentMinValue ? Number(currentMinValue) : null
			);
			const [maxVal, setMaxVal] = useState<number | null>(
				currentMaxValue ? Number(currentMaxValue) : null
			);
			const isFilterActive = !!(currentMinValue || currentMaxValue);

			useEffect(() => {
				setMinVal(currentMinValue ? Number(currentMinValue) : null);
				setMaxVal(currentMaxValue ? Number(currentMaxValue) : null);
			}, [currentMinValue, currentMaxValue]);

			const handleApply = () => {
				if (minVal !== null) {
					queryParams.set(minParamKey, String(minVal));
				} else {
					queryParams.delete(minParamKey);
				}
				if (maxVal !== null) {
					queryParams.set(maxParamKey, String(maxVal));
				} else {
					queryParams.delete(maxParamKey);
				}
				queryParams.set('page', '1');
				navigate({ search: queryParams.toString() });
				confirm();
			};

			const handleReset = () => {
				setMinVal(null);
				setMaxVal(null);
				queryParams.delete(minParamKey);
				queryParams.delete(maxParamKey);
				queryParams.set('page', '1');
				navigate({ search: queryParams.toString() });
				clearFilters?.();
				confirm();
			};

			return (
				<div
					style={{ padding: 8, width: 280 }}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<InputNumber
						placeholder={`Min ${label}`}
						value={minVal}
						onChange={(value) => setMinVal(value)}
						onPressEnter={handleApply}
						style={{ width: '100%', marginBottom: 8 }}
						step={0.25}
						min={hasValidRange ? min : undefined}
						max={hasValidRange ? max : undefined}
						suffix={unit}
					/>
					<InputNumber
						placeholder={`Max ${label}`}
						value={maxVal}
						onChange={(value) => setMaxVal(value)}
						onPressEnter={handleApply}
						style={{ width: '100%', marginBottom: 8 }}
						step={0.25}
						min={hasValidRange ? min : undefined}
						max={hasValidRange ? max : undefined}
						suffix={unit}
					/>
					{hasValidRange && (
						<>
							<Slider
								range
								min={min}
								max={max}
								step={0.25}
								value={[minVal ?? min, maxVal ?? max]}
								onChange={(value) => {
									setMinVal(value[0]);
									setMaxVal(value[1]);
								}}
								tooltip={{ formatter: (value) => `${value}"` }}
								style={{ marginBottom: 8 }}
							/>
							<div
								style={{
									fontSize: '12px',
									color: '#999',
									marginBottom: 8,
									textAlign: 'center',
								}}
							>
								Available: {min.toFixed(2)}&quot; - {max.toFixed(2)}&quot;
							</div>
						</>
					)}
					<div
						style={{
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
						}}
					>
						<Button
							onClick={handleReset}
							size="small"
							type="text"
							disabled={!isFilterActive}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleApply}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: () => (
			<FilterFilled
				style={{
					color: currentMinValue || currentMaxValue ? '#1890ff' : undefined,
				}}
			/>
		),
	};
};

export const getColumnDimensionsFilter = ({
	queryParams,
	navigate,
	dimensionRanges,
}: {
	queryParams: URLSearchParams;
	navigate: any;
	dimensionRanges?: any;
}) => {
	const getRange = (
		range: { min?: number; max?: number } | undefined,
		defaultMax = 1000
	) => ({
		min: range?.min ?? 0,
		max: range?.max ?? defaultMax,
	});

	const heightRange = getRange(dimensionRanges?.height);
	const widthRange = getRange(dimensionRanges?.width);
	const outerDiameterRange = getRange(dimensionRanges?.outer_diameter);
	const innerDiameterRange = getRange(dimensionRanges?.inner_diameter);
	const wallRange = getRange(dimensionRanges?.wall, 5);

	const rangeConfigs = [
		{
			label: 'Height',
			minParamKey: 'height_min',
			maxParamKey: 'height_max',
			min: heightRange.min,
			max: heightRange.max,
		},
		{
			label: 'Width',
			minParamKey: 'width_min',
			maxParamKey: 'width_max',
			min: widthRange.min,
			max: widthRange.max,
		},
		{
			label: 'Outer Diameter',
			minParamKey: 'outer_diameter_min',
			maxParamKey: 'outer_diameter_max',
			min: outerDiameterRange.min,
			max: outerDiameterRange.max,
		},
		{
			label: 'Inner Diameter',
			minParamKey: 'inner_diameter_min',
			maxParamKey: 'inner_diameter_max',
			min: innerDiameterRange.min,
			max: innerDiameterRange.max,
		},
		{
			label: 'Wall',
			minParamKey: 'wall_min',
			maxParamKey: 'wall_max',
			min: wallRange.min,
			max: wallRange.max,
		},
	];

	const legacyCombinedDimensionParams = [
		'height_od_min',
		'height_od_max',
		'width_id_min',
		'width_id_max',
	];
	const isFilterActive =
		rangeConfigs.some(
			({ minParamKey, maxParamKey }) =>
				queryParams.get(minParamKey) || queryParams.get(maxParamKey)
		) || legacyCombinedDimensionParams.some((key) => queryParams.get(key));

	const DimensionsFilterDropdown = ({ confirm, clearFilters }: any) => {
		const [values, setValues] = useState<Record<string, number | null>>(() =>
			rangeConfigs.reduce<Record<string, number | null>>((acc, config) => {
				acc[config.minParamKey] = queryParams.get(config.minParamKey)
					? Number(queryParams.get(config.minParamKey))
					: null;
				acc[config.maxParamKey] = queryParams.get(config.maxParamKey)
					? Number(queryParams.get(config.maxParamKey))
					: null;
				return acc;
			}, {})
		);

		useEffect(() => {
			setValues(
				rangeConfigs.reduce<Record<string, number | null>>((acc, config) => {
					acc[config.minParamKey] = queryParams.get(config.minParamKey)
						? Number(queryParams.get(config.minParamKey))
						: null;
					acc[config.maxParamKey] = queryParams.get(config.maxParamKey)
						? Number(queryParams.get(config.maxParamKey))
						: null;
					return acc;
				}, {})
			);
		}, [queryParams]);

		const setRangeValue = (key: string, value: number | null) => {
			setValues((prev) => ({ ...prev, [key]: value }));
		};

		const handleApply = () => {
			legacyCombinedDimensionParams.forEach((key) => queryParams.delete(key));
			rangeConfigs.forEach(({ minParamKey, maxParamKey }) => {
				const minValue = values[minParamKey];
				const maxValue = values[maxParamKey];

				if (minValue !== null && minValue !== undefined) {
					queryParams.set(minParamKey, String(minValue));
				} else {
					queryParams.delete(minParamKey);
				}

				if (maxValue !== null && maxValue !== undefined) {
					queryParams.set(maxParamKey, String(maxValue));
				} else {
					queryParams.delete(maxParamKey);
				}
			});

			queryParams.set('page', '1');
			navigate({ search: queryParams.toString() });
			confirm();
		};

		const handleReset = () => {
			legacyCombinedDimensionParams.forEach((key) => queryParams.delete(key));
			rangeConfigs.forEach(({ minParamKey, maxParamKey }) => {
				queryParams.delete(minParamKey);
				queryParams.delete(maxParamKey);
			});
			setValues(
				rangeConfigs.reduce<Record<string, null>>((acc, config) => {
					acc[config.minParamKey] = null;
					acc[config.maxParamKey] = null;
					return acc;
				}, {})
			);
			queryParams.set('page', '1');
			navigate({ search: queryParams.toString() });
			clearFilters?.();
			confirm();
		};

		return (
			<div
				style={{ padding: 10, width: 320 }}
				onKeyDown={(e) => e.stopPropagation()}
			>
				{rangeConfigs.map(({ label, minParamKey, maxParamKey, min, max }) => (
					<div
						key={label}
						style={{
							marginTop: 6,
							paddingTop: 6,
							borderTop: '1px solid #f0f0f0',
						}}
					>
						<Typography.Text strong>{label}</Typography.Text>
						<div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
							<InputNumber
								placeholder="Min"
								value={values[minParamKey]}
								onChange={(value) => setRangeValue(minParamKey, value)}
								onPressEnter={handleApply}
								step={0.25}
								min={min}
								max={max}
								suffix="in"
								style={{ width: '50%' }}
							/>
							<InputNumber
								placeholder="Max"
								value={values[maxParamKey]}
								onChange={(value) => setRangeValue(maxParamKey, value)}
								onPressEnter={handleApply}
								step={0.25}
								min={min}
								max={max}
								suffix="in"
								style={{ width: '50%' }}
							/>
						</div>
						<Typography.Text type="secondary" style={{ fontSize: 11 }}>
							Available: {Number(min).toFixed(3)}&quot; -{' '}
							{Number(max).toFixed(3)}&quot;
						</Typography.Text>
					</div>
				))}
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						gap: 8,
						marginTop: 12,
					}}
				>
					<Button
						onClick={handleReset}
						size="small"
						type="text"
						disabled={!isFilterActive}
						style={{
							color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
						}}
					>
						Reset
					</Button>
					<Button
						type="primary"
						onClick={handleApply}
						size="small"
						style={{ width: 90 }}
					>
						Ok
					</Button>
				</div>
			</div>
		);
	};

	return {
		filterDropdown: (props: any) => <DimensionsFilterDropdown {...props} />,
		filterIcon: () => (
			<FilterFilled style={{ color: isFilterActive ? '#1890ff' : undefined }} />
		),
	};
};

export const getColumnCertificateFilter = ({ queryParams, navigate }: any) => {
	const isFilterActive = !!queryParams.get('has_certificate');

	return {
		filterDropdown: ({ confirm, clearFilters }: any) => {
			const [tempValue, setTempValue] = useState<string | null>(
				queryParams.get('has_certificate') || null
			);

			useEffect(() => {
				setTempValue(queryParams.get('has_certificate') || null);
			}, [queryParams.get('has_certificate')]);

			const handleOk = () => {
				if (tempValue) {
					queryParams.set('has_certificate', tempValue);
				} else {
					queryParams.delete('has_certificate');
				}
				queryParams.set('page', '1');
				navigate({ search: queryParams.toString() });
				confirm();
			};

			const handleReset = () => {
				setTempValue(null);
				queryParams.delete('has_certificate');
				queryParams.set('page', '1');
				navigate({ search: queryParams.toString() });
				clearFilters?.();
				confirm();
			};

			return (
				<div
					style={{ padding: 8, width: 200 }}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<Radio.Group
						value={tempValue}
						onChange={(e) => setTempValue(e.target.value)}
						style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
					>
						<Radio value="True">Has Certificate</Radio>
						<Radio value="False">No Certificate</Radio>
					</Radio.Group>

					<div
						style={{
							borderTop: '1px solid #f0f0f0',
							paddingTop: 8,
							marginTop: 8,
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
						}}
					>
						<Button
							onClick={handleReset}
							size="small"
							type="text"
							disabled={!isFilterActive}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleOk}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: () => (
			<FilterFilled
				style={{
					color: queryParams.get('has_certificate') ? '#1890ff' : undefined,
				}}
			/>
		),
	};
};

export const getColumnCategoryTypeFilter = (props: {
	queryParams: URLSearchParams;
	navigate: any;
	categories: any[];
	types: any[];
}) => {
	return {
		filterDropdown: ({ confirm }: any) => {
			const currentCat = props.queryParams.get('category') || undefined;
			const rawTypes = props.queryParams.get('types') || '';
			const currentTypes = rawTypes ? rawTypes.split(',').filter(Boolean) : [];

			const [localCat, setLocalCat] = useState<string | undefined>(currentCat);
			const [localTypes, setLocalTypes] = useState<string[]>(currentTypes);

			useEffect(() => {
				const urlCat = props.queryParams.get('category') || undefined;
				const urlRawTypes = props.queryParams.get('types') || '';
				const urlTypes = urlRawTypes
					? urlRawTypes.split(',').filter(Boolean)
					: [];

				setLocalCat(urlCat);
				setLocalTypes(urlTypes);
			}, [props.queryParams]);

			const filteredTypes = localCat
				? props.types.filter(
						(t) =>
							t.material_category?.id === localCat || t.category_id === localCat
				  )
				: props.types;

			const handleOk = () => {
				if (localCat) props.queryParams.set('category', localCat);
				else props.queryParams.delete('category');

				if (localTypes.length > 0) {
					props.queryParams.set('types', localTypes.join(','));
				} else {
					props.queryParams.delete('types');
				}

				props.queryParams.set('page', '1');
				props.navigate({ search: props.queryParams.toString() });
				confirm();
			};

			const handleReset = () => {
				setLocalCat(undefined);
				setLocalTypes([]);
				props.queryParams.delete('category');
				props.queryParams.delete('types');
				props.queryParams.set('page', '1');
				props.navigate({ search: props.queryParams.toString() });
				confirm();
			};

			const isFilterActive = !!(localCat || localTypes.length > 0);

			return (
				<div
					style={{ padding: 8, minWidth: 280, width: 280 }}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<Select
						style={{ width: '100%', marginBottom: 8 }}
						placeholder="Select Category"
						value={localCat || undefined}
						allowClear
						onChange={(val) => {
							setLocalCat(val || undefined);
							setLocalTypes([]);

							if (val) props.queryParams.set('category', val);
							else props.queryParams.delete('category');

							props.queryParams.delete('types');
							props.queryParams.set('page', '1');
							props.navigate({ search: props.queryParams.toString() });
						}}
					>
						{props.categories.map((cat) => (
							<Select.Option key={cat.id} value={cat.id}>
								{cat.display_name}
							</Select.Option>
						))}
					</Select>

					<div
						style={{
							border: '1px solid #d9d9d9',
							borderRadius: 4,
							maxHeight: 160,
							overflowY: 'auto',
							padding: '4px 8px',
							marginBottom: 8,
						}}
					>
						<Checkbox.Group
							value={localTypes}
							onChange={(values) => setLocalTypes(values as string[])}
							style={{
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{filteredTypes.map((t) => (
								<Checkbox
									key={t.id}
									value={t.id}
									style={{ margin: '4px 0', marginLeft: 0 }}
								>
									{t.display_name}
								</Checkbox>
							))}
						</Checkbox.Group>
					</div>

					<div
						style={{
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
							marginTop: 8,
						}}
					>
						<Button
							onClick={handleReset}
							size="small"
							type="text"
							disabled={!isFilterActive}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleOk}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: () => {
			const hasFilter =
				props.queryParams.get('category') || props.queryParams.get('types');
			return (
				<FilterFilled style={{ color: hasFilter ? '#1890ff' : undefined }} />
			);
		},
	};
};
